import React, { createRef, type ReactElement, useEffect, useRef, useState } from 'react';
import type { SectionProps, SidebarProps } from './Sidebar.types';

export const Sidebar: React.FC<SidebarProps> = (props) => {
    const [sizingInitialized, setSizingInitialized] = useState(false);
    const sectionElements = useRef<{ [key: string]: React.RefObject<HTMLDivElement> }>({});
    const rootElement = useRef<HTMLDivElement>(null);
    const sectionHeights = useRef<{ [key: string]: number }>({});

    useEffect(() => {
        updateSidebarSize();
    });

    const updateSidebarSize = () => {
        sectionHeights.current = sectionHeights.current || {};
        Object.keys(sectionElements.current).forEach((key) => {
            sectionHeights.current[key] = sectionElements.current[key].current.scrollHeight || sectionHeights.current[key] || 0;
        });

        const tabsHeight = 40 + React.Children.toArray(props.children).length * 40 + (props.infoButton ? 40 : 0);
        if (rootElement.current) {
            rootElement.current.style.maxHeight = ((!props.open ? tabsHeight : sectionHeights.current[props.activeSection] + 41) || 0) + 'px';
            rootElement.current.style.minHeight = tabsHeight + 'px';
        }

        if (!sizingInitialized) {
            setSizingInitialized(true);
        }
    };

    const onTabClick = (sectionId?: string) => {
        if (sectionId === props.activeSection) {
            props.toggleOpen();
            return;
        }

        props.onTabChange?.(sectionId);
    };

    const renderTabsContainer = (children: ReactElement<SectionProps>[]): ReactElement => {
        return (
            <div className="sidebar-tabs">
                <div className="sidebar-tabs__main-area" role="tablist">
                    {renderToggle()}
                    {children.map(renderSectionTab)}
                </div>
                {props.infoButton}
            </div>
        );
    };

    const renderToggle = (): ReactElement => {
        return (
            <button
                className="sidebar-tabs__toggle sidebar-tabs__tab-button unstyled-button"
                onClick={props.toggleOpen}
                aria-label="Toggle sidebar"
                aria-expanded={props.open}
                aria-controls={props.activeSection}
            >
                {props.toggleIcon}
            </button>
        );
    };

    const renderSectionTab = (section: ReactElement<SectionProps>): ReactElement => {
        let icon;
        if (typeof section.props.icon === 'string') icon = <i className={section.props.icon} />;
        else if (typeof section.props.icon === 'object') icon = section.props.icon;
        const isSelected = section.props.id === props.activeSection;
        return (
            <button
                key={section.props.id}
                id={'tab_' + section.props.id}
                aria-label={section.props.heading}
                role="tab"
                aria-selected={isSelected}
                aria-controls={section.props.id}
                className={'unstyled-button sidebar-tabs__tab-button' + (isSelected ? ' active' : '') + (section.props.disabled ? ' disabled' : '')}
                onClick={() => !section.props.disabled && onTabClick(section.props.id)}
            >
                {icon}
            </button>
        );
    };

    const renderHeader = (section: ReactElement<SectionProps>): ReactElement => {
        const disabled = section.props.disabled ? ' disabled' : '';
        return <h4 className={'sidebar-header' + disabled}>{section.props.heading}</h4>;
    };

    const renderSection = (section: ReactElement<SectionProps>): ReactElement => {
        sectionElements.current[section.props.id] = createRef();

        return (
            <div
                key={section.props.id}
                id={section.props.id}
                role="tabpanel"
                aria-labelledby={'tab_' + section.props.id}
                className={'sidebar-section' + (!sizingInitialized || section.props.id === props.activeSection ? ' active' : '')}
                ref={sectionElements.current[section.props.id]}
            >
                {section.props.children}
            </div>
        );
    };

    const collapsedClass = !props.open ? 'collapsed' : '';

    const children = React.Children.toArray(props.children) as ReactElement<SectionProps>[];

    return (
        <div id={props.id} className={`sidebar ${collapsedClass}`} ref={rootElement}>
            {renderTabsContainer(children)}
            <div className="sidebar-header-container">{renderHeader(children.filter((child) => child.props.id === props.activeSection)[0])}</div>
            <div className="sidebar-content">{children.map(renderSection)}</div>
        </div>
    );
};
